import React from "react";
import {
  DownloadContainer,
  SavingAdvicesMessage,
} from "../../ProductPrice/AdviceMessages/AdviceDiff/styled";

import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import { useSelector } from "react-redux";
import { DiffMessage } from "../../ProductPrice/AdviceMessages/AdviceDiff/DiffMessage";
import { useIsAdvicePriceLoading } from "../../ProductPrice/hooks/stateSelectors";
import {
  AdviceFeedback,
  Description,
  ObliqueDescription,
} from "../../adviceStyled";
import {
  AkaAdvicePriceFormValues,
  SubmitAction,
} from "../AkaAdviceCards/interfaces";
import { AkaAdviceScreenshot } from "../AkaAdviceScreenshot/AkaAdviceScreenshot";
import { SortedNeed } from "../utils";
import { AdviceDiffConsent } from "./AdviceDiffConsent";
import {
  AdviceDiffType,
  DiffMessageValues,
  generateDiffMessage,
} from "./adviceDiffMessagesUtils";

interface Props {
  sortedNeeds: SortedNeed[];
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  akaAdviceScreenshot: AkaAdviceScreenshot;
}

export const AdviceDiffMessages = ({
  sortedNeeds,
  formProps,
  akaAdviceScreenshot,
}: Props): React.ReactElement => {
  const [{ formText }, lang] = useProductTexts(
    getFMSProductName(ProductBackendName.ADVICE),
  );

  const isAdvisor = useIsAdvisorSelector();
  const priceError = useSelector(
    (state: ReduxState) => state.akaAdvice.advicePriceCalculationResults.error,
  );
  const isPriceLoading = useIsAdvicePriceLoading();
  const addProductToCart = useSelector(
    (state: ReduxState) => state.addProductToCart,
  );

  const messages: DiffMessageValues[] = sortedNeeds
    .map((sortedNeed) =>
      generateDiffMessage(
        isAdvisor,
        sortedNeed,
        formProps.values,
        formText.advice,
        lang,
      ),
    )
    .filter(isDefined);

  const adviceDiffMessages = formText.advice.adviceMessages;

  return (
    <>
      <AdviceFeedback id="adviceFeedback"></AdviceFeedback>
      <SavingAdvicesMessage className="AdviceDiffCard">
        {!isAdvisor && (
          <>
            <Description fw={700} size="16px" className="AdviceDiffText">
              {adviceDiffMessages.header[lang]}
            </Description>
            <Description fw={400} size="16px" className="AdviceDiffText">
              {adviceDiffMessages.description.desc1[lang]}
            </Description>
            {messages.map(
              ({
                productCoverageName,
                diffType,
                message,
                additionalMessages,
              }) => (
                <div
                  key={`${productCoverageName}_diffMessages`}
                  className={`${productCoverageName}_diffMessages`}
                >
                  <DiffMessage
                    key={`${productCoverageName}_checkedToPurchaseDiff`}
                    diffType={diffType}
                    message={message}
                  />
                  {additionalMessages.map((additionalMessage, key) => (
                    <DiffMessage
                      key={`${productCoverageName}_additionalMessage_${key}`}
                      diffType={AdviceDiffType.INFO}
                      message={additionalMessage}
                    />
                  ))}
                </div>
              ),
            )}
          </>
        )}
        <AdviceDiffConsent />
        <DownloadContainer
          className="AdviceDownloadContainer"
          withMargin={!isAdvisor}
        >
          <ThemedButton
            type="button"
            id="advice__download-button"
            trackId={buttonTrackId("Download the entire advice")}
            iconLeft="download"
            variant="text"
            onClick={async () => {
              await formProps.setFieldValue(
                "submitAction",
                SubmitAction.DOWNLOAD_ADVICE,
              );
              await formProps.submitForm();
              await formProps.setFieldValue("submitAction", undefined);
            }}
            disabled={
              isPriceLoading ||
              isDefined(priceError) ||
              addProductToCart.isLoading
            }
          >
            {"Last ned hele anbefalingen (PDF)"}
          </ThemedButton>
          <ObliqueDescription fw={400} size="16px">
            {isAdvisor
              ? "Når du har lastet ned dette dokumentet kan du også finne det igjen på kunden i OnBase"
              : "Når du har lastet ned dette dokumentet kan du også finne det igjen på storebrand.no/arkiv"}
          </ObliqueDescription>
          {akaAdviceScreenshot.screenshotIsLoading && <ThemedSpinner />}
        </DownloadContainer>
      </SavingAdvicesMessage>
    </>
  );
};
